import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// Body-level portal target for overlays that must escape a host container with overflow/containment.
// Carries the app root's classes because MFE CSS is prefixed with .orgchart-mfe.
const EscapePortalContext = createContext<HTMLElement | null>(null);

export const useEscapePortalContainer = () => useContext(EscapePortalContext);

// Inline because a stylesheet rule would get prefixed to `.orgchart-mfe .ocn-escape-portal-layer`.
const LAYER_STYLE =
  "position: fixed; left: 0; top: 0; width: 0; height: 0; container-type: normal; z-index: 2147483000;";

interface EscapePortalContainerProviderProps {
  className?: string;
  children: ReactNode;
}

export const EscapePortalContainerProvider = ({
  className,
  children
}: EscapePortalContainerProviderProps) => {
  const [layer, setLayer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = document.createElement("div");
    node.className = ["ocn-escape-portal-layer", className].filter(Boolean).join(" ");
    node.style.cssText = LAYER_STYLE;
    document.body.appendChild(node);
    setLayer(node);

    return () => {
      node.remove();
      setLayer(null);
    };
  }, [className]);

  return <EscapePortalContext.Provider value={layer}>{children}</EscapePortalContext.Provider>;
};
